<script setup lang="ts">
import {InteractionGroup, interactionKey} from "@/drug-interaction/composables/useInteractionGroups";
import {ALERT_TYPE} from "@/drug-interaction/interfaces";

const props = defineProps<{ group: InteractionGroup, checkedKeys: Set<string>, activeTypes: Set<ALERT_TYPE>}>();
const emit = defineEmits(['update-active-types-by-line']);
const TYPE_LABELS: Record<ALERT_TYPE, string> = {
    DRUG_INTERACTION: 'Interactions',
    PHYSICO_CHEMICAL_INTERACTION: 'Interactions physico-chimiques',
    FOOD_INTERACTION: 'Interactions alimentaires',
    SIDE_EFFECT: 'Effets indésirables',
    WARNING: 'Mises en garde',
    PRECAUTION: 'Précautions',
    CONTRA_INDICATION: 'Contre-indications',
    ALLERGY: 'Allergies',
    POSOLOGY: 'Posologies',
    HAS: 'Problèmes de santé',
    INDICATOR: 'Indicateurs',
    REDUNDANT_ACTIVE_INGREDIENT: 'Principes actifs redondants',
    SAME_DRUG: 'Doublons',
    DISPENSING_RISK: 'Risques à la dispensation',
    SURVEILLANCE: 'Surveillances',
    EXONERATION: 'Exonérations',
    PRESCRIPTION_CONTEXT: 'Contextes de prescription',
};
/**
 * Get counts of interactions by type for a given group, used to display filter tags with counts.
 */
const tagsForGroup = (group: InteractionGroup): { type: ALERT_TYPE; count: number; checkedCount: number }[] => {
    const countsByType = new Map<ALERT_TYPE, { count: number; checkedCount: number }>();
    for (const i of group.interactions) {
        const entry = countsByType.get(i.type) ?? {count: 0, checkedCount: 0};
        entry.count++;

        if (props.checkedKeys.has(interactionKey(i))) {
            entry.checkedCount++;
        }

        countsByType.set(i.type, entry);
    }
    return Array.from(countsByType.entries()).map(([type, {count, checkedCount}]) => ({type, count, checkedCount}));
};


/**
 * Toggle the active state of an alert type for a given line.
 */
const toggleTypeFilter = (group: InteractionGroup, type: ALERT_TYPE) => {
    const active = new Set(props.activeTypes);

    if (active.has(type)) {
        active.delete(type);
    } else {
        active.add(type);
    }

    emit('update-active-types-by-line', group.item.id, active);

};
</script>

<template>
  <div
    v-if="tagsForGroup(group).length > 1"
    class="flex flex-wrap gap-1.5"
  >
    <button
      v-for="tag in tagsForGroup(group)"
      :key="tag.type"
      type="button"
      class="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs leading-none font-medium transition-colors cursor-pointer"
      :class="activeTypes.has(tag.type)
        ? 'border-primary bg-primary text-white'
        : tag.checkedCount > 0
          ? 'border-primary bg-white text-primary hover:bg-primary/5'
          : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'"
      @click="toggleTypeFilter(group, tag.type)"
    >
      {{ TYPE_LABELS[tag.type] ?? tag.type }}
      <span
        v-if="activeTypes.has(tag.type)"
        class="inline-flex items-center justify-center rounded-full w-4 h-4 text-[10px] font-bold bg-white/20 text-white"
      >
        {{ tag.count }}
      </span>
      <template v-else-if="tag.checkedCount > 0">
        <span
          class="inline-flex items-center justify-center rounded-full w-4 h-4 text-[10px] font-bold bg-primary text-white"
        >
          {{ tag.checkedCount }}
        </span>
      </template>
      <span
        v-else
        class="inline-flex items-center justify-center rounded-full w-4 h-4 text-[10px] font-bold bg-gray-100 text-gray-500"
      >
        {{ tag.count }}
      </span>
    </button>
  </div>
</template>

<style scoped>

</style>